import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TruckGlyph } from '../../../LogisticsPage/panels/InventoryRowGlyphs';
import styles from './StockCart.module.css';

/** One material waiting to be ordered, as the panel lists it. */
export interface ICartEntry {
  claimId: string;
  materialName: string;
  /** The amount the checkout would order — already rounded, with its unit. */
  amount: string;
}

interface IStockCartTruckProps {
  entries: ICartEntry[];
  /** Opens that material's own form (ruling 2026-09-16: a row in the panel is
   *  the way back into what it books). */
  onOpen: (claimId: string) => void;
  /** Opens the checkout — the same screen the bar's button opens. */
  onReview: () => void;
}

/**
 * The delivery truck beside "← Back to materials", carrying the count of
 * materials the task still has to order (mock browse-cart.html, ruling
 * 2026-09-16). Pressing it drops a panel listing them: a row opens that
 * material, and the panel's button goes to the review.
 *
 * The same truck the Logistics inventory rows use, so "something is coming"
 * looks the same in both places. Rendered only when the cart holds something.
 */
export function StockCartTruck({ entries, onOpen, onReview }: IStockCartTruckProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const label = t('scheduler.panel.stock.cart.toOrder', { count: entries.length });

  // A click anywhere else, or Escape, puts the panel away — the same
  // blur-away idiom the tab's combos use, so nothing is trapped open.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <div className={styles.truckWrap} ref={wrap}>
      <button
        type="button"
        className={open ? `${styles.truck} ${styles.truckOpen}` : styles.truck}
        aria-label={label}
        aria-expanded={open}
        // The truck sits inside the open row's focus boundary (beside "Back
        // to materials"), and that row folds when focus leaves it. Taking
        // focus on click would make the next click anywhere read as leaving
        // the row — the form being built vanished into the cart. So a click
        // does not focus the truck; the keyboard still reaches it.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        <TruckGlyph size={19} />
        <span className={styles.badge}>{entries.length}</span>
      </button>
      {open && (
        <div className={styles.panel}>
          <p className={styles.panelHead}>{label}</p>
          {entries.map((entry) => (
            <button
              key={entry.claimId}
              type="button"
              className={styles.panelRow}
              onClick={() => {
                setOpen(false);
                onOpen(entry.claimId);
              }}
            >
              <span>{entry.materialName}</span>
              <span className={styles.panelQty}>{entry.amount}</span>
            </button>
          ))}
          <button
            type="button"
            className={styles.panelReview}
            onClick={() => {
              setOpen(false);
              onReview();
            }}
          >
            {t('scheduler.panel.stock.cart.review')}
          </button>
        </div>
      )}
    </div>
  );
}
